import type { TaskPlanState } from '../../types/chat';

type InjectedUserMessage = {
    content: string;
};

const TASK_PLAN_EVENT_NAME = 'skf-widget-task-plan';
const TERMINAL_TASK_PLAN_EVENTS = new Set(['cleared', 'completed', 'cancelled']);
const TERMINAL_TASK_PLAN_STATUSES = new Set(['completed', 'cancelled']);

export function normalizeTaskPlanChunk(body: unknown): TaskPlanState | null {
    if (!body || typeof body !== 'object') {
        return null;
    }

    const record = body as Record<string, unknown>;
    const planId = getString(record.planId) ?? '';
    const status = getString(record.status) ?? '';
    const currentTaskId = getString(record.currentTaskId);
    const event = getString(record.event);
    const transitionReason = getString(record.transitionReason);
    const waitingReason = getString(record.waitingReason);
    const replanCount = typeof record.replanCount === 'number' ? record.replanCount : undefined;
    const generateSummary = typeof record.generateSummary === 'boolean' ? record.generateSummary : undefined;
    const tasks = normalizeTaskPlanItems(record.tasks);
    const nextTask = normalizeTaskPlanItems(record.nextTask ? [record.nextTask] : [])[0];

    if (!planId.length || !status.length) {
        return null;
    }

    return {
        planId,
        status,
        currentTaskId,
        event,
        transitionReason,
        waitingReason,
        replanCount,
        generateSummary,
        tasks,
        nextTask
    };
}

export function normalizeInjectedUserMessage(body: unknown): InjectedUserMessage | null {
    if (typeof body === 'string') {
        const trimmed = body.trim();
        return trimmed.length > 0 ? { content: trimmed } : null;
    }

    if (!body || typeof body !== 'object') {
        return null;
    }

    const record = body as Record<string, unknown>;
    const content = getString(record.content) ?? getString(record.text);
    if (!content) {
        return null;
    }

    return { content };
}

export function emitTaskPlanEvent(plan: TaskPlanState): void {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') {
        return;
    }

    if (!plan.event) {
        return;
    }

    window.dispatchEvent(
        new CustomEvent(TASK_PLAN_EVENT_NAME, {
            detail: plan
        })
    );
}

export function mergeTaskPlanState(previous: TaskPlanState | null, incoming: TaskPlanState): TaskPlanState {
    if (!previous || previous.planId !== incoming.planId) {
        return incoming;
    }

    if (!Array.isArray(incoming.tasks) || incoming.tasks.length === 0) {
        return {
            ...incoming,
            tasks: previous.tasks,
            nextTask: incoming.nextTask ?? previous.nextTask,
            currentTaskId: incoming.currentTaskId ?? previous.currentTaskId
        };
    }

    return incoming;
}

export function shouldClearTaskPlan(plan: TaskPlanState, previous: TaskPlanState | null): boolean {
    if (!plan) {
        return true;
    }

    if (previous && previous.planId && plan.planId && previous.planId !== plan.planId) {
        return false;
    }

    const event = plan.event?.toLowerCase() ?? '';
    if (TERMINAL_TASK_PLAN_EVENTS.has(event)) {
        return true;
    }

    const status = plan.status?.toLowerCase() ?? '';
    if (TERMINAL_TASK_PLAN_STATUSES.has(status)) {
        return true;
    }

    return false;
}

function normalizeTaskPlanItems(value: unknown): TaskPlanState['tasks'] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .map((item): TaskPlanState['tasks'][number] | null => {
            if (!item || typeof item !== 'object') {
                return null;
            }

            const record = item as Record<string, unknown>;
            const id = getString(record.id) ?? '';
            const title = getString(record.title) ?? '';
            const agentId = getString(record.agentId) ?? '';
            const status = getString(record.status) ?? '';

            if (!id.length || !title.length || !agentId.length || !status.length) {
                return null;
            }

            const normalizedTask: TaskPlanState['tasks'][number] = {
                id,
                title,
                agentId,
                status
            };

            return normalizedTask;
        })
        .filter((item): item is TaskPlanState['tasks'][number] => Boolean(item));
}

function getString(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim().length > 0 ? value : undefined;
}
